import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { Box, DashPlaceholder, Flex, Text, Button, Image, ModalContext, Toggle } from "dash-ui";
import { PropertyStatusesEnum } from "dash-common";

import {
  GetPropertyByIdQuery,
  useChangeVerifiedStatusInPropertyMutation,
} from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";
import { BadgePropertyStatusChange } from "containers/properties/components/BadgePropertyStatusChange";
import { PropertyDetailsModalsEnum } from "pages/properties/PropertyDetailsPage/PropertyDetailsPage.constants";
import { stickyTopBarStyles } from "containers/properties/helpers/stickyTopBarStyles";

const HeaderWrapper = styled(Box)<{ top?: number }>`
  ${stickyTopBarStyles}
  z-index: 5;
`;

interface IProps {
  property: GetPropertyByIdQuery["getPropertyById"];
}

function PropertyDetailsHeader({ property }: IProps) {
  const { t: tLabels } = useTranslation("common", {
    keyPrefix: "labels",
  });
  const { t: tPropertyPage } = useTranslation("pages", {
    keyPrefix: "propertyPage",
  });

  const { toggleModal, setPayload } = useContext(ModalContext);
  const [isMarkedAsVerified, setIsMarkedAsVerified] = useState(property.isMarkedAsVerified);
  const theme = useTheme();

  const {
    _id: propertyId,
    primaryOrganizationId,
    name,
    status,
    mainPropertyMediaFile,
    marketArea,
    location,
  } = property;
  const file = mainPropertyMediaFile?.files[0];

  const [changeVerifiedStatusInProperty] = useChangeVerifiedStatusInPropertyMutation();

  function onDeletePropertyClick() {
    setPayload(property);
    toggleModal(true, PropertyDetailsModalsEnum.DELETE_PROPERTY_MODAL);
  }

  function onSwitchIsMarkedAsVerifiedToggle(isVerified: boolean) {
    setIsMarkedAsVerified(isVerified);
    changeVerifiedStatusInProperty({
      variables: { propertyId, isMarkedAsVerified: isVerified },
    });
  }

  return (
    <HeaderWrapper p={4} w="100%" pos="sticky" top={107}>
      <Flex flexDirection="row" alignItems="center">
        {file ? (
          <Image src={file.thumbnailFile?.publicUrl || ""} size={80} fit="cover" />
        ) : (
          <DashPlaceholder size={80} borderRadius={4} />
        )}

        <Flex flexDirection="column" flex={1} w="100%" ml={3}>
          <Flex flexDirection="column">
            <Text xl bold>
              {name}
            </Text>
          </Flex>
          <Flex flexDirection="row" mt={2} justifyContent="space-between" w="100%">
            <Flex alignItems="center">
              <Button
                mr={3}
                medium
                onClick={() => {
                  window?.open(RoutesPath.PropertyPreview.replace(":id", propertyId), "_blank");
                }}
              >{tLabels`preview`}</Button>
              <Button
                variation="outline"
                mr={3}
                medium
                onClick={() => {
                  window?.open(
                    RoutesPath.PropertyPublicPreview.replace(":id", propertyId),
                    "_blank"
                  );
                }}
              >
                Public Preview
              </Button>
              <BadgePropertyStatusChange
                currentStatus={status as PropertyStatusesEnum}
                propertyId={propertyId}
                primaryOrganizationId={primaryOrganizationId || undefined}
                marketArea={marketArea}
                location={location}
              />
              <Box ml={4}>
                {/* TODO: Fix @ts-ignore */}
                {/* There is huge difference between available props on dash-ui and cms-portal */}
                <Toggle
                  // @ts-ignore
                  color={theme.colors.g20}
                  checked={isMarkedAsVerified}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onSwitchIsMarkedAsVerifiedToggle(e.target.checked);
                  }}
                  label={(isMarkedAsVerified ? tLabels`verified` : tLabels`unverified`) as string}
                  labelProps={{
                    ...(isMarkedAsVerified && {
                      color: theme.colors.g20,
                      bold: true,
                    }),
                  }}
                />
              </Box>
            </Flex>
            <Button
              variation="flat"
              onClick={onDeletePropertyClick}
            >{tPropertyPage`deleteProperty`}</Button>
          </Flex>
        </Flex>
      </Flex>
    </HeaderWrapper>
  );
}

export default PropertyDetailsHeader;
